import React from 'react';

import type { EditorActions } from '@atlaskit/editor-core';

import type { OnboardingEditorState } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	ONBOARDING_EDITOR_DRAFT_BANNER_EXPERIENCE,
	ONBOARDING_LAND_IN_EDITOR_BANNER_EXPERIENCE,
	ExperienceStart,
} from '@confluence/experience-tracker';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary';
import { useShouldHideFeatureForCfind3673 } from '@confluence/experiment-cfind-3673-update-template-onboarding';
import type { FlagsStateContainer } from '@confluence/flags';

import { EditorDraftBanner } from './EditorDraftBanner';
import { LandInEditorBanner } from './LandInEditorBanner';
import { useOnboardingBannerLogic } from './hooks/useOnboardingBannerLogic';

type VersionType = { number?: number };

export type EditorBannerContainerProps = {
	isContentCreationPanelOpen: boolean;
	version?: number | VersionType;
	contentId: string;
	isShowingOfflineBanner: boolean;
	flags?: FlagsStateContainer;
	spaceKey: string;
	setIsOnboardingBannerDataLoaded: (value: boolean) => void;
	onClickOpenContentCreationPanel: () => void;
	editorActions: EditorActions;
	handleTitleChange: (newTitle: string) => void;
};

export type EditorBannerProps = {
	deserializedData: OnboardingEditorState | undefined;
	showingOfflineBanner: boolean;
	setIsVisible: (value: boolean) => void;
};

export const BaseOnboardingEditorBanner = (props: EditorBannerContainerProps) => {
	const {
		contentId,
		version,
		isShowingOfflineBanner,
		spaceKey,
		editorActions,
		setIsOnboardingBannerDataLoaded,
		handleTitleChange,
		onClickOpenContentCreationPanel,
	} = props;

	const {
		isVisible,
		setIsVisible,
		isLandInEditorBannerShown,
		showingOfflineBanner,
		onboardingStateData,
		onboardingStateError,
		deserializedData,
		editorOnboardingEligiblity,
		wasDraftBannerSeen,
		labelsError,
		hasQueryErrors,
	} = useOnboardingBannerLogic({
		contentId,
		version,
		isShowingOfflineBanner,
		spaceKey,
		setIsOnboardingBannerDataLoaded,
	});

	const { shouldHideFeatureForCfind3673, isLoading: isLoadingShouldHideFeatureForCfind3673 } =
		useShouldHideFeatureForCfind3673();

	// note: unauthorized errors are handled in the queries themselves
	if (onboardingStateError) {
		return <ErrorDisplay error={onboardingStateError} />;
	} else if (labelsError) {
		return <ErrorDisplay error={labelsError} />;
	}

	if (isLoadingShouldHideFeatureForCfind3673 || shouldHideFeatureForCfind3673) {
		return null;
	}

	if (!isVisible) {
		// TODO: Manage logic in a separate component https://hello.atlassian.net/browse/CONFONBO-1674
		// These are all conditions where we should not block the Editor Help Center from rendering
		const finishedLoading: boolean =
			// there are any errors in the queries
			onboardingStateError ||
			labelsError ||
			hasQueryErrors ||
			// the user is not eligible for the experiment
			(onboardingStateData && !editorOnboardingEligiblity) ||
			wasDraftBannerSeen;

		if (finishedLoading) {
			setIsOnboardingBannerDataLoaded(true);
		}
		return null;
	}

	const [BannerComponent, experienceName] = isLandInEditorBannerShown
		? [LandInEditorBanner, ONBOARDING_LAND_IN_EDITOR_BANNER_EXPERIENCE]
		: [EditorDraftBanner, ONBOARDING_EDITOR_DRAFT_BANNER_EXPERIENCE];

	return (
		<>
			{isLandInEditorBannerShown && <ExperienceStart name={experienceName} />}
			<BannerComponent
				{...props}
				showingOfflineBanner={showingOfflineBanner}
				setIsVisible={setIsVisible}
				deserializedData={onboardingStateData && deserializedData}
				editorActions={editorActions}
				onClickOpenContentCreationPanel={onClickOpenContentCreationPanel}
				handleTitleChange={handleTitleChange}
			/>
		</>
	);
};

export const OnboardingEditorBanner = (props) => {
	return (
		<TransparentErrorBoundary attribution={Attribution.FLYWHEEL_ACTIVATION}>
			<BaseOnboardingEditorBanner {...props} />
		</TransparentErrorBoundary>
	);
};
